import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CT, EVAP, EY, FREEZE, FS, MAX_CORALS, SY } from "../sim/constants";
import {
  applyBoids,
  computeEcosystemStats,
  getSSP245Rate,
  getSaveRate,
  mkCorals,
  mkFish,
  mkWavePoints,
  resetCoralIdCounter,
  stepSim,
  toIso,
} from "../sim/helpers";

const WEATHER_CONFIG = {
  sunny: { minYears: 5, maxYears: 11, label: "☀️ Sunny" },
  cloudy: { minYears: 4, maxYears: 9, label: "☁️ Cloudy" },
  raining: { minYears: 4, maxYears: 8, label: "🌧️ Raining" },
  thunderstorm: { minYears: 3, maxYears: 6, label: "⛈️ Thunderstorm" },
  sunshower: { minYears: 3, maxYears: 6, label: "🌦️ Sun + Rain" },
};

function randYears(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function weightedPick(items) {
  const total = items.reduce((s, i) => s + i.weight, 0);
  let roll = Math.random() * total;
  for (const item of items) {
    roll -= item.weight;
    if (roll <= 0) return item.type;
  }
  return items[items.length - 1].type;
}

function pickNextWeather(currentWeather, waterLevel, disallowed = []) {
  const dryness = Math.max(0, (waterLevel - 60) / 40);
  const wetBoost = 1 + Math.max(0, (60 - waterLevel) / 80);
  const blocked = new Set(disallowed);

  const options = [
    { type: "sunny", weight: 2.5 + dryness * 2 },
    { type: "cloudy", weight: 2 + dryness },
    { type: "raining", weight: 1.6 * wetBoost },
    { type: "thunderstorm", weight: 0.8 * wetBoost },
    { type: "sunshower", weight: 1.1 * wetBoost },
  ].filter((o) => !blocked.has(o.type)).map((o) => {
    if (o.type === currentWeather) return { ...o, weight: o.weight * 0.4 };
    return o;
  });

  return weightedPick(options);
}

export default function Simulator({ onLock }) {
  const cvRef = useRef(null);
  const anRef = useRef(null);
  const fishR = useRef(mkFish(80, false));
  const bubR = useRef(Array.from({ length: 20 }, () => ({ x: Math.random() * 960, y: 0, spd: 0.2 + Math.random() * 0.5, sz: 1 + Math.random() * 3, op: 0.1 + Math.random() * 0.3 })));
  const waveR = useRef(mkWavePoints(40));
  const rainR = useRef(Array.from({ length: 80 }, () => ({ x: Math.random() * 960, y: Math.random() * 500, spd: 8 + Math.random() * 6, len: 10 + Math.random() * 15 })));

  const init = useMemo(() => mkCorals(), []);
  const [year, setYear] = useState(SY);
  const [oT, setOT] = useState(27);
  const [corals, setCorals] = useState(init);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(0.032);
  const [expanded, setExpanded] = useState(false);
  const [startTemp, setStartTemp] = useState(27);
  const [waterLevel, setWaterLevel] = useState(100);
  const [weatherType, setWeatherType] = useState("sunny");
  const [nextWeatherType, setNextWeatherType] = useState("cloudy");
  const [mode, setMode] = useState("custom");
  const [simEnded, setSimEnded] = useState(false);
  const [simplified, setSimplified] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [canvasWarnings, setCanvasWarnings] = useState([]);
  const [fishTick, setFishTick] = useState(0);
  const [canvasFullscreen, setCanvasFullscreen] = useState(false);

  const warnIdRef = useRef(0);
  const snapshotsRef = useRef(new Map());
  const ratesRef = useRef(new Map());
  const pRef = useRef(null);
  const lastWarnRef = useRef(null);
  const weatherRef = useRef({ type: "sunny", yearsLeft: 8, nextType: "cloudy", recent: ["sunny", "cloudy"] });
  const weatherTransitionRef = useRef({ prevType: "sunny", progress: 1, startTime: 0, duration: 3000 });

  useEffect(() => {
    snapshotsRef.current.set(SY, { corals: init.map((c) => ({ ...c })), fish: fishR.current.map((f) => ({ ...f })), temp: startTemp });
  }, [init, startTemp]);

  const sortedCorals = useMemo(() => [...corals].sort((a, b) => a.gx + a.gy - (b.gx + b.gy)), [corals]);

  const stats = computeEcosystemStats(corals, fishR.current, startTemp, oT);

  const addWarn = useCallback((text, color) => {
    const id = ++warnIdRef.current;
    setCanvasWarnings((w) => [...w, { id, text, color, birth: Date.now() }]);
    setTimeout(() => setCanvasWarnings((w) => w.filter((x) => x.id !== id)), 3000);
  }, []);

  const doStep = useCallback(
    (prevC, nextTemp, nextYear) => {
      const res = stepSim(prevC, fishR.current, nextTemp, startTemp);
      snapshotsRef.current.set(nextYear, { corals: res.corals, fish: res.fish.map((f) => ({ ...f })), temp: nextTemp });
      setCorals(res.corals);
      fishR.current = res.fish;
      setFishTick((t) => t + 1);
      return res;
    },
    [startTemp]
  );

  const stepWeather = useCallback(
    ({ reset = false } = {}) => {
      let nextType = weatherRef.current.type;
      let nextYears = weatherRef.current.yearsLeft - 1;
      let queuedNext = weatherRef.current.nextType;
      let recent = weatherRef.current.recent;

      if (reset) {
        weatherTransitionRef.current = { prevType: "sunny", progress: 1, startTime: 0, duration: 3000 };
        nextType = "sunny";
        nextYears = randYears(WEATHER_CONFIG.sunny.minYears, WEATHER_CONFIG.sunny.maxYears);
        recent = ["sunny", "cloudy"];
        queuedNext = pickNextWeather("sunny", waterLevel, recent);
      } else if (nextYears <= 0) {
        weatherTransitionRef.current = {
          prevType: weatherRef.current.type,
          progress: 0,
          startTime: performance.now(),
          duration: 3000,
        };
        nextType = queuedNext;
        nextYears = randYears(WEATHER_CONFIG[nextType].minYears, WEATHER_CONFIG[nextType].maxYears);
        recent = [nextType, recent[0]];
        queuedNext = pickNextWeather(nextType, waterLevel, recent);
        addWarn(`Weather shifted: ${WEATHER_CONFIG[nextType].label}`, "#8fc8e8");
      }

      weatherRef.current = { type: nextType, yearsLeft: nextYears, nextType: queuedNext, recent };
      setWeatherType(nextType);
      setNextWeatherType(queuedNext);
    },
    [addWarn, waterLevel]
  );

  const advance = useCallback(() => {
    if (simEnded) return;

    setYear((py) => {
      if (py >= EY) {
        setPlaying(false);
        return EY;
      }

      const ny = py + 1;
      stepWeather();

      setOT((pt) => {
        let nextRate = rate;
        const prevC = snapshotsRef.current.get(py)?.corals || init;

        if (mode === "current") nextRate = getSSP245Rate(ny);
        else if (mode === "save") nextRate = getSaveRate(prevC, pt);

        const nextTemp = pt + nextRate;
        ratesRef.current.set(ny, nextRate);

        if (nextTemp <= FREEZE || nextTemp >= EVAP) {
          doStep(prevC, nextTemp, ny);
          setSimEnded(true);
          setPlaying(false);
          addWarn(nextTemp <= FREEZE ? "🧊 OCEAN FROZEN — Simulation ended" : "🔥 OCEAN EVAPORATED — Simulation ended", nextTemp <= FREEZE ? "#a0d0f0" : "#f0a050");
          return nextTemp;
        }

        const wk = nextTemp <= 5 ? "f5" : nextTemp <= 15 ? "c15" : nextTemp >= 50 ? "e50" : nextTemp >= 35 ? "h35" : null;
        if (wk && lastWarnRef.current !== wk) {
          lastWarnRef.current = wk;
          addWarn(
            nextTemp <= 5
              ? `❄️ DANGER: ${nextTemp.toFixed(1)}°C — Approaching freeze`
              : nextTemp <= 15
                ? `❄️ Cold stress: ${nextTemp.toFixed(1)}°C`
                : nextTemp >= 50
                  ? `🌡️ DANGER: ${nextTemp.toFixed(1)}°C — Evaporation risk`
                  : `🌡️ Heat stress: ${nextTemp.toFixed(1)}°C`,
            nextTemp <= 15 ? "#80c0e8" : "#e8a040"
          );
        }

        doStep(prevC, nextTemp, ny);
        return nextTemp;
      });

      return ny;
    });
  }, [addWarn, doStep, init, mode, rate, simEnded, stepWeather]);

  const scrubToYear = useCallback(
    (targetYear) => {
      if (simEnded) return;
      setPlaying(false);

      const snap = snapshotsRef.current.get(targetYear);
      if (snap) {
        setYear(targetYear);
        setOT(snap.temp);
        setCorals(snap.corals);
        fishR.current = snap.fish;
        setFishTick((t) => t + 1);
        return;
      }

      let closest = SY;
      for (const [y] of snapshotsRef.current) {
        if (y <= targetYear && y > closest) closest = y;
      }

      const base = snapshotsRef.current.get(closest);
      if (!base) return;

      let cc = base.corals.map((c) => ({ ...c }));
      let cf = base.fish.map((f) => ({ ...f }));
      let ct = base.temp;

      for (let y = closest + 1; y <= targetYear; y++) {
        const r = ratesRef.current.get(y) || rate;
        ct += r;
        const res = stepSim(cc, cf, ct, startTemp);
        cc = res.corals;
        cf = res.fish;
        snapshotsRef.current.set(y, { corals: cc, fish: cf, temp: ct });
      }

      setYear(targetYear);
      setOT(ct);
      setCorals(cc);
      fishR.current = cf;
      setFishTick((t) => t + 1);
    },
    [rate, simEnded, startTemp]
  );

  useEffect(() => {
    if (playing && !simEnded) pRef.current = setInterval(advance, 1000 / speed);
    return () => clearInterval(pRef.current);
  }, [playing, advance, simEnded, speed]);

  const reset = useCallback(() => {
    setPlaying(false);
    setYear(SY);
    setOT(startTemp);
    setRate(0.032);
    setSimEnded(false);
    setCanvasWarnings([]);
    lastWarnRef.current = null;
    resetCoralIdCounter();
    stepWeather({ reset: true });

    const fresh = init.map((c) => ({ ...c, alg: 100, alive: true, age: c.age, size: c.size, cod: null, ta: 0 }));
    setCorals(fresh);
    fishR.current = mkFish(80, false);
    setFishTick((t) => t + 1);

    snapshotsRef.current = new Map();
    snapshotsRef.current.set(SY, { corals: fresh, fish: fishR.current.map((f) => ({ ...f })), temp: startTemp });
    ratesRef.current = new Map();
  }, [init, startTemp, stepWeather]);

  const tC = stats.tempChange;
  const frz = oT <= FREEZE;
  const evp = oT >= EVAP;
  const iceAmt = oT < 10 ? Math.min(1, (10 - oT) / 15) : 0;
  const evAmt = oT > 60 ? Math.min(1, (oT - 60) / 40) : 0;
  const surfaceVisible = waterLevel < 100;
  const waterY = Math.round(((100 - waterLevel) / 100) * 500 * 0.6);
  const isSunny = weatherType === "sunny";
  const isRaining = weatherType === "raining" || weatherType === "thunderstorm" || weatherType === "sunshower";
  const isThunderstorm = weatherType === "thunderstorm";
  const isSunshower = weatherType === "sunshower";
  const isCloudy = weatherType === "cloudy" || isRaining;

  let effectiveRate = rate;
  if (mode === "current") effectiveRate = getSSP245Rate(year);
  else if (mode === "save") effectiveRate = getSaveRate(corals, oT);

  useEffect(() => {
    const cv = cvRef.current;
    if (!cv) return;

    const ctx = cv.getContext("2d");
    const W = cv.width;
    const H = cv.height;
    const wY = waterY;

    // ── Transition helpers ──
    function lerpN(a, b, t) { return a + (b - a) * t; }
    function easeInOut(t) { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; }
    function hexToRgb(h) {
      return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
    }
    function lerpColor(h1, h2, t) {
      const [r1, g1, b1] = hexToRgb(h1), [r2, g2, b2] = hexToRgb(h2);
      return `rgb(${Math.round(r1 + (r2 - r1) * t)},${Math.round(g1 + (g2 - g1) * t)},${Math.round(b1 + (b2 - b1) * t)})`;
    }
    function wProp(type) {
      switch (type) {
        case "thunderstorm": return { dark: 1,   rain: 1,   storm: 1, cloud: 1,   sun: 0, rainbow: 0 };
        case "raining":      return { dark: 0.5, rain: 0.8, storm: 0, cloud: 0.6, sun: 0, rainbow: 0 };
        case "cloudy":       return { dark: 0.5, rain: 0,   storm: 0, cloud: 0.6, sun: 0, rainbow: 0 };
        case "sunshower":    return { dark: 0,   rain: 0.5, storm: 0, cloud: 0,   sun: 1, rainbow: 1 };
        default:             return { dark: 0,   rain: 0,   storm: 0, cloud: 0,   sun: 1, rainbow: 0 };
      }
    }
    // Mutable blend values updated each frame in render()
    const bd = { dark: 0, rain: 0, storm: 0, cloud: 0, sun: 1, rainbow: 0, waveAmp: 1, waveFreq: 1 };

    function drawSky() {
      if (wY <= 0) return;

      // Lerped sky gradient
      const g = ctx.createLinearGradient(0, 0, 0, wY);
      g.addColorStop(0, lerpColor("#4a7ab0", "#1f2432", bd.dark));
      if (bd.dark < 0.75) {
        g.addColorStop(0.6, lerpColor("#6aa0d0", "#252a38", bd.dark / 0.75));
      }
      g.addColorStop(1, lerpColor("#8ac0e8", "#2d3344", bd.dark));
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, wY);

      // Clouds — count and colour blend continuously
      const cloudCount = Math.round(lerpN(4, 7, bd.cloud));
      const cr = Math.round(lerpN(255, 35, bd.cloud));
      const cg = Math.round(lerpN(255, 35, bd.cloud));
      const cb = Math.round(lerpN(255, 50, bd.cloud));
      const ca = lerpN(0.3, 0.75, bd.cloud);
      ctx.fillStyle = `rgba(${cr},${cg},${cb},${ca})`;
      for (let i = 0; i < cloudCount; i++) {
        const packed = bd.cloud > 0.2;
        ctx.beginPath();
        ctx.ellipse(
          packed ? 80 + i * 160 : 120 + i * 220,
          wY * (packed ? 0.25 : 0.3) + (packed ? 0 : i * 8),
          packed ? 60 + (i % 2) * 15 : 40 + i * 10,
          packed ? 22 + (i % 3) * 5 : 12 + i * 3,
          0, 0, Math.PI * 2
        );
        ctx.fill();
      }

      // Sun — fades in/out
      if (bd.sun > 0.01) {
        const sunX = W + 45, sunY = -45, sunR = 105, sa = bd.sun;
        for (let i = 0; i < 6; i++) {
          const ray = ctx.createLinearGradient(W - 220 + i * 18, 0, W - 120 + i * 30, wY + 30);
          ray.addColorStop(0, `rgba(255,235,170,${(0.22 * sa).toFixed(3)})`);
          ray.addColorStop(1, "rgba(255,235,170,0)");
          ctx.fillStyle = ray;
          ctx.beginPath();
          ctx.moveTo(W - 210 + i * 14, 0);
          ctx.lineTo(W - 130 + i * 26, 0);
          ctx.lineTo(W - 40 + i * 36, wY + 15);
          ctx.lineTo(W - 130 + i * 22, wY + 15);
          ctx.closePath();
          ctx.fill();
        }
        const halo = ctx.createRadialGradient(sunX, sunY, 40, sunX, sunY, 190);
        halo.addColorStop(0, `rgba(255,245,200,${(0.55 * sa).toFixed(3)})`);
        halo.addColorStop(1, "rgba(255,245,200,0)");
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(sunX, sunY, 190, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(255,230,140,${(0.95 * sa).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(sunX, sunY, sunR, 0, Math.PI * 2);
        ctx.fill();
      }

      // Rainbow — fades in/out for sunshower
      if (bd.rainbow > 0.01) {
        const cx = W * 0.5, cy = wY * 0.95;
        const rainbowColors = [[255,80,80],[255,150,70],[255,220,90],[100,220,120],[80,170,255],[170,120,255]];
        for (let i = 0; i < rainbowColors.length; i++) {
          const [r, rg, rb] = rainbowColors[i];
          ctx.strokeStyle = `rgba(${r},${rg},${rb},${(0.45 * bd.rainbow).toFixed(3)})`;
          ctx.lineWidth = 7;
          ctx.beginPath();
          ctx.arc(cx, cy, 150 + i * 9, Math.PI * 1.05, Math.PI * 1.95);
          ctx.stroke();
        }
      }
    }

    function computeWaves(t) {
      if (wY <= 2) return;
      waveR.current.forEach((p) => {
        p.baseY = wY + Math.sin(t * 0.002 * bd.waveFreq + p.phase) * p.amp * bd.waveAmp + Math.sin(t * 0.005 * bd.waveFreq + p.phase * 2) * p.amp * 0.4 * bd.waveAmp;
      });
    }

    function drawWavySurface() {
      if (wY <= 2) return;
      const wp = waveR.current;
      const maxDist = 60;

      for (let i = 0; i < wp.length; i++) {
        for (let j = i + 1; j < wp.length; j++) {
          const dx = wp[j].x - wp[i].x;
          const dy = wp[j].baseY - wp[i].baseY;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < maxDist) {
            const a = (1 - d / maxDist) * (isRaining ? 0.5 : 0.35);
            ctx.strokeStyle = isThunderstorm ? `rgba(210,220,235,${a})` : isRaining ? `rgba(180,200,220,${a})` : `rgba(120,200,255,${a})`;
            ctx.lineWidth = isRaining ? 1.5 : 1;
            ctx.beginPath();
            ctx.moveTo(wp[i].x, wp[i].baseY);
            ctx.lineTo(wp[j].x, wp[j].baseY);
            ctx.stroke();
          }
        }
      }

      const pc = isThunderstorm ? "210,220,230" : isRaining ? "180,200,225" : "140,220,255";
      wp.forEach((p) => {
        ctx.fillStyle = `rgba(${pc},${isRaining ? 0.4 : 0.6})`;
        ctx.beginPath();
        ctx.arc(p.x, p.baseY, isRaining ? 3 : 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(${pc},${isRaining ? 0.12 : 0.15})`;
        ctx.beginPath();
        ctx.arc(p.x, p.baseY, isRaining ? 7 : 5, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    function bg() {
      const wt = Math.max(0, wY);
      const fillWavy = (style) => {
        ctx.fillStyle = style;
        if (surfaceVisible && wY > 2) {
          ctx.beginPath();
          const wp = waveR.current;
          if (wp.length > 0) {
            ctx.moveTo(0, wp[0].baseY || wY);
            for (let i = 0; i < wp.length - 1; i++) {
              const cx2 = (wp[i].x + wp[i + 1].x) / 2;
              const cy2 = ((wp[i].baseY || wY) + (wp[i + 1].baseY || wY)) / 2;
              ctx.quadraticCurveTo(wp[i].x, wp[i].baseY || wY, cx2, cy2);
            }
            ctx.lineTo(W, wp[wp.length - 1].baseY || wY);
          } else {
            ctx.moveTo(0, wY);
            ctx.lineTo(W, wY);
          }
          ctx.lineTo(W, H);
          ctx.lineTo(0, H);
          ctx.closePath();
          ctx.fill();
        } else {
          ctx.fillRect(0, wt, W, H - wt);
        }
      };

      if (evAmt > 0) {
        const et = wt + evAmt * (H - wt) * 0.7;
        const sG = ctx.createLinearGradient(0, wt, 0, et);
        sG.addColorStop(0, `rgb(${180 + evAmt * 70},${90 + evAmt * 50},40)`);
        sG.addColorStop(1, `rgb(${200 + evAmt * 40},130,50)`);
        ctx.fillStyle = sG;
        ctx.fillRect(0, wt, W, et - wt);

        const wG = ctx.createLinearGradient(0, et, 0, H);
        wG.addColorStop(0, `rgb(${40 + evAmt * 80},${45 + evAmt * 35},${25 + evAmt * 15})`);
        wG.addColorStop(1, `rgb(${55 + evAmt * 60},${35 + evAmt * 25},15)`);
        ctx.fillStyle = wG;
        ctx.fillRect(0, et, W, H - et);
      } else {
        const g = ctx.createLinearGradient(0, wt, 0, H);
        if (iceAmt > 0) {
          g.addColorStop(0, `rgb(${170 + iceAmt * 70},${205 + iceAmt * 40},${235 + iceAmt * 15})`);
          g.addColorStop(0.2, `rgb(${100 + iceAmt * 80},${160 + iceAmt * 60},${210 + iceAmt * 30})`);
          g.addColorStop(0.6, `rgb(${15 + iceAmt * 50},${50 + iceAmt * 60},${100 + iceAmt * 50})`);
          g.addColorStop(1, `rgb(${10 + iceAmt * 25},${35 + iceAmt * 40},${60 + iceAmt * 35})`);
        } else {
          g.addColorStop(0, "#0a1628");
          g.addColorStop(0.3, "#0d2847");
          g.addColorStop(0.7, "#0f3460");
          g.addColorStop(1, "#143a5c");
        }
        fillWavy(g);
      }

      const fg = ctx.createLinearGradient(0, H - 60, 0, H);
      fg.addColorStop(0, "rgba(194,170,120,0)");
      fg.addColorStop(0.4, "rgba(194,170,120,.12)");
      fg.addColorStop(1, "rgba(160,140,100,.2)");
      ctx.fillStyle = fg;
      ctx.fillRect(0, H - 60, W, 60);
    }

    function drawCoral(c) {
      const [x, y] = toIso(c.gx, c.gy, c.ox, c.oy, W, H, wY);
      if (x < -15 || x > W + 15 || y < wY - 5 || y > H + 15) return;

      let r;
      let g;
      let b;

      if (!c.alive) {
        if (c.cod === "frozen") {
          r = 160;
          g = 190;
          b = 210;
        } else if (c.cod === "evaporated") {
          r = 120;
          g = 100;
          b = 70;
        } else {
          const tf = c.ta / 100;
          r = Math.round(80 - tf * 30);
          g = Math.round(70 + tf * 30);
          b = Math.round(60 - tf * 20);
        }
      } else if (c.alg > 80) {
        const af = c.alg / 100;
        r = Math.round(c.type.c[0] * af + 240 * (1 - af));
        g = Math.round(c.type.c[1] * af + 240 * (1 - af));
        b = Math.round(c.type.c[2] * af + 245 * (1 - af));
      } else if (c.alg > 50) {
        const sf = (c.alg - 50) / 30;
        r = Math.round(c.type.c[0] * 0.6 + 180 * (1 - sf) + 60 * sf);
        g = Math.round(c.type.c[1] * 0.5 + 160 * (1 - sf));
        b = Math.round(Math.min(255, c.type.c[2] * 0.5 + 200 * (1 - sf) + 80));
      } else {
        r = Math.round(220 + (240 - 220) * (1 - c.alg / 50));
        g = r;
        b = Math.round(225 + (245 - 225) * (1 - c.alg / 50));
      }

      const s = c.size;
      ctx.save();
      ctx.translate(x, y);
      ctx.fillStyle = `rgb(${r},${g},${b})`;

      if (c.type.s === "branch") {
        ctx.beginPath();
        ctx.ellipse(0, 0, s * 0.4, s, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(-s * 0.4, -s * 0.3, s * 0.3, s * 0.7, -0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(s * 0.4, -s * 0.2, s * 0.3, s * 0.6, 0.3, 0, Math.PI * 2);
        ctx.fill();
      } else if (c.type.s === "round") {
        ctx.beginPath();
        ctx.ellipse(0, 0, s, s * 0.65, 0, 0, Math.PI * 2);
        ctx.fill();
      } else if (c.type.s === "plate") {
        ctx.beginPath();
        ctx.ellipse(0, 0, s * 1.2, s * 0.5, 0, 0, Math.PI * 2);
        ctx.fill();
      } else if (c.type.s === "pillar") {
        ctx.beginPath();
        ctx.roundRect(-s * 0.35, -s * 1.1, s * 0.7, s * 1.3, [s * 0.3, s * 0.3, 2, 2]);
        ctx.fill();
      } else if (c.type.s === "fan") {
        ctx.beginPath();
        ctx.moveTo(0, s * 0.5);
        ctx.quadraticCurveTo(-s * 1.2, -s * 0.3, -s * 0.3, -s * 1.2);
        ctx.quadraticCurveTo(0, -s * 1.4, s * 0.3, -s * 1.2);
        ctx.quadraticCurveTo(s * 1.2, -s * 0.3, 0, s * 0.5);
        ctx.fill();
      }

      ctx.fillStyle = "rgba(0,0,0,.1)";
      ctx.beginPath();
      ctx.ellipse(2, s * 0.3, s * 0.7, s * 0.18, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    function drawFish() {
      if (frz || evp) return;
      const all = fishR.current;
      if (!all || !all.length) return;

      applyBoids(all, W, H, wY);
      for (let i = 0; i < all.length; i++) {
        const f = all[i];
        if (!f.alive || f.x < -50 || f.x > W + 50 || f.y < 0 || f.y > H) continue;

        const sz = f.sp.sz;
        const [r, g, b] = f.sp.c;
        const tx = f.vx >= 0 ? -1 : 1;

        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.beginPath();
        ctx.ellipse(f.x, f.y, sz * 1.5, sz * 0.6, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.beginPath();
        ctx.moveTo(f.x + tx * sz * 1.3, f.y);
        ctx.lineTo(f.x + tx * sz * 2, f.y - sz * 0.5);
        ctx.lineTo(f.x + tx * sz * 2, f.y + sz * 0.5);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = "white";
        ctx.beginPath();
        ctx.arc(f.x - tx * sz * 0.6, f.y - sz * 0.1, sz * 0.18, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = "black";
        ctx.beginPath();
        ctx.arc(f.x - tx * sz * 0.65, f.y - sz * 0.1, sz * 0.09, 0, Math.PI * 2);
        ctx.fill();

        if (f.sp.isClown) {
          ctx.fillStyle = "white";
          ctx.fillRect(f.x - sz * 0.1, f.y - sz * 0.6, sz * 0.12, sz * 1.2);
          ctx.fillRect(f.x + sz * 0.4, f.y - sz * 0.5, sz * 0.1, sz);
        }
      }
    }

    function drawBubbles(t) {
      if (frz || evp) return;
      bubR.current.forEach((b) => {
        if (!b.y || b.y < wY) b.y = H;
        b.y -= b.spd;
        b.x += Math.sin(t * 0.001 + b.x) * 0.12;
        if (b.y < wY + 5) {
          b.y = H;
          b.x = Math.random() * W;
        }
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.sz, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(150,220,255,${b.op})`;
        ctx.fill();
      });
    }

    function drawRain() {
      if (bd.rain < 0.01 || wY <= 2) return;
      const alpha = lerpN(0.35, 0.55, bd.storm) * bd.rain;
      ctx.strokeStyle = `rgba(${bd.storm > 0.3 ? "210,220,235" : "180,200,220"},${alpha.toFixed(3)})`;
      ctx.lineWidth = lerpN(1, 1.4, bd.storm);
      const speedMult = lerpN(1, 1.8, bd.storm);
      const windDrift = lerpN(2, 3.5, bd.storm);
      rainR.current.forEach((r) => {
        r.y += r.spd * speedMult;
        r.x -= windDrift;
        if (r.y > wY) { r.y = -r.len; r.x = Math.random() * W; }
        if (r.x < -20) r.x = W + 20;
        ctx.beginPath();
        ctx.moveTo(r.x, r.y);
        ctx.lineTo(r.x + 2, r.y + r.len);
        ctx.stroke();
      });
    }

    function drawLightning() {
      if (bd.storm < 0.01 || Math.random() > 0.02 * bd.storm) return;
      const startX = Math.random() * W;
      let x = startX, y = 0;
      ctx.strokeStyle = `rgba(240,245,255,${(0.95 * bd.storm).toFixed(3)})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, y);
      while (y < wY + 120) {
        x += (Math.random() - 0.5) * 30;
        y += 18 + Math.random() * 20;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.fillStyle = `rgba(230,240,255,${(0.08 * bd.storm).toFixed(3)})`;
      ctx.fillRect(0, 0, W, H);
    }

    function drawEnd() {
      const t = frz ? "🧊 OCEAN FROZEN" : "🔥 OCEAN EVAPORATED";
      const c = frz ? "rgba(200,230,255,.9)" : "rgba(255,200,100,.9)";
      const c2 = frz ? "rgba(180,210,240,.8)" : "rgba(255,180,80,.8)";
      ctx.fillStyle = c;
      ctx.font = "bold 28px Georgia";
      ctx.textAlign = "center";
      ctx.fillText(t, W / 2, H / 2);
      ctx.font = "14px Georgia";
      ctx.fillStyle = c2;
      ctx.fillText("Press Reset to try again", W / 2, H / 2 + 25);
    }

    function drawWarnings() {
      const now = Date.now();
      canvasWarnings.forEach((w, i) => {
        const age = now - w.birth;
        const a = age < 500 ? age / 500 : age > 2500 ? 1 - (age - 2500) / 500 : 1;
        if (a <= 0) return;

        const yP = H - 50 - i * 35;
        ctx.fillStyle = `rgba(0,0,0,${a * 0.6})`;
        ctx.beginPath();
        ctx.roundRect(W / 2 - 200, yP - 14, 400, 28, [14]);
        ctx.fill();
        ctx.strokeStyle = `${w.color}${Math.round(a * 180).toString(16).padStart(2, "0")}`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.roundRect(W / 2 - 200, yP - 14, 400, 28, [14]);
        ctx.stroke();
        ctx.fillStyle = `${w.color}${Math.round(a * 255).toString(16).padStart(2, "0")}`;
        ctx.font = "bold 12px Georgia";
        ctx.textAlign = "center";
        ctx.fillText(w.text, W / 2, yP + 4);
      });
    }

    function drawSteam(t) {
      if (evAmt <= 0) return;
      const base = wY + evAmt * (H - wY) * 0.7;
      ctx.fillStyle = `rgba(255,255,255,${evAmt * 0.07})`;
      for (let i = 0; i < Math.floor(evAmt * 25); i++) {
        ctx.beginPath();
        ctx.arc((i * 137.5 + t * 0.02) % W, base - 15 - Math.sin(t * 0.003 + i) * 20 - Math.random() * 40, 3 + Math.random() * 8, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function render(t) {
      // ── Compute transition blend ──
      const tr = weatherTransitionRef.current;
      const trP = tr.progress >= 1 ? 1 : easeInOut(Math.min(1, (t - tr.startTime) / tr.duration));
      if (trP >= 1) tr.progress = 1;

      const wp0 = wProp(tr.prevType);
      const wp1 = wProp(weatherType);
      bd.dark     = lerpN(wp0.dark,    wp1.dark,    trP);
      bd.rain     = lerpN(wp0.rain,    wp1.rain,    trP);
      bd.storm    = lerpN(wp0.storm,   wp1.storm,   trP);
      bd.cloud    = lerpN(wp0.cloud,   wp1.cloud,   trP);
      bd.sun      = lerpN(wp0.sun,     wp1.sun,     trP);
      bd.rainbow  = lerpN(wp0.rainbow, wp1.rainbow, trP);
      bd.waveAmp  = lerpN(wp0.storm ? 4.2 : wp0.rain ? 2.8 : 1, wp1.storm ? 4.2 : wp1.rain ? 2.8 : 1, trP);
      bd.waveFreq = lerpN(wp0.storm ? 3.2 : wp0.rain ? 2.1 : 1, wp1.storm ? 3.2 : wp1.rain ? 2.1 : 1, trP);

      ctx.clearRect(0, 0, W, H);
      computeWaves(t);
      drawSky();
      bg();
      sortedCorals.forEach(drawCoral);
      drawBubbles(t);
      drawFish();
      drawSteam(t);
      drawWavySurface();
      drawRain();
      drawLightning();
      if (simEnded) drawEnd();
      drawWarnings();

      // Weather label — visible whenever sky is noticeably dark or rainy, fades naturally
      const labelVis = Math.max(bd.cloud, bd.rain);
      if (labelVis > 0.08 && wY > 2) {
        ctx.fillStyle = `rgba(${bd.storm > 0.5 ? "220,225,235" : "180,200,220"},${(labelVis * (bd.storm > 0.5 ? 0.85 : 0.7)).toFixed(3)})`;
        ctx.font = "bold 12px Georgia";
        ctx.textAlign = "left";
        ctx.fillText(WEATHER_CONFIG[weatherType].label.toUpperCase(), 10, wY - 5);
      }

      anRef.current = requestAnimationFrame(render);
    }

    anRef.current = requestAnimationFrame(render);
    return () => cancelAnimationFrame(anRef.current);
  }, [canvasWarnings, fishTick, frz, evAmt, evp, iceAmt, isCloudy, isRaining, isSunny, isSunshower, isThunderstorm, simEnded, sortedCorals, surfaceVisible, waterY, weatherType]);

  const tempColor = oT < 15 ? "#60a8e0" : oT < 24 ? "#5ac4e0" : oT < 28 ? "#4aba6a" : oT < 32 ? "#d4a843" : "#e06060";
  const tCS = `${tC >= 0 ? "+" : ""}${tC.toFixed(2)}`;
  const hsL = stats.avgAlgae > 80 ? "Healthy" : stats.avgAlgae > 50 ? "Stressed" : stats.avgAlgae > 20 ? "Bleaching" : "Critical";
  const hsC = stats.avgAlgae > 80 ? "#4aba6a" : stats.avgAlgae > 50 ? "#d4a843" : stats.avgAlgae > 20 ? "#e07a50" : "#e04040";
  const erStr = `${effectiveRate >= 0 ? "+" : ""}${effectiveRate.toFixed(3)}`;

  const S = {
    background: "rgba(10,22,40,.92)",
    borderRadius: "10px",
    padding: "8px",
    border: "1px solid #1e3f5e",
    boxShadow: "0 3px 12px rgba(0,0,0,.4)",
    textAlign: "center",
    minWidth: 0,
    flex: "1 1 70px",
    overflow: "hidden",
  };

  const L = { fontSize: "9px", color: "#5a8aaa", fontWeight: "600", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "3px" };
  const V = (c) => ({ fontSize: "16px", fontWeight: "700", color: c, fontFamily: "'Courier New',monospace", lineHeight: 1.1 });
  const statCard = { ...S, minWidth: 0 };

  const slider = (label, min, max, value, onChange, display, color, grad) => (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <span style={{ fontSize: "8px", color: "#5a8aaa", fontWeight: "600", letterSpacing: "1px", textTransform: "uppercase", width: "38px", flexShrink: 0 }}>{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={onChange}
        style={{
          flex: 1,
          minWidth: 0,
          height: "5px",
          borderRadius: "3px",
          appearance: "none",
          background: grad || `linear-gradient(90deg,#1a6a8a 0%,#1a6a8a ${((value - min) / (max - min)) * 100}%,#1a2a3a ${((value - min) / (max - min)) * 100}%,#1a2a3a 100%)`,
          outline: "none",
          cursor: "pointer",
        }}
      />
      <span style={{ fontSize: "9px", fontFamily: "'Courier New',monospace", fontWeight: "700", width: "55px", flexShrink: 0, textAlign: "right", color: color || "#7a9bba", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{display}</span>
    </div>
  );

  const modeBtn = (k, l) => (
    <button
      key={k}
      onClick={() => setMode(k)}
      style={{
        padding: "5px 10px",
        fontSize: "10px",
        fontFamily: "'Georgia',serif",
        fontWeight: "600",
        border: mode === k ? "2px solid #5cb8d6" : "2px solid #2a4a6b",
        borderRadius: "7px",
        cursor: "pointer",
        color: mode === k ? "#fff" : "#8ab4d4",
        background: mode === k ? "linear-gradient(180deg,#1a5a7a,#0d3a54)" : "linear-gradient(180deg,#15293e,#0e1e30)",
      }}
    >
      {l}
    </button>
  );

  return (
    <div style={{ width: "100%", minHeight: "100vh", background: "linear-gradient(180deg,#060e1a,#0a1930,#0d2040)", fontFamily: "'Georgia',serif", color: "#d4e5f7", display: "flex", flexDirection: "column", alignItems: "center", padding: canvasFullscreen ? "0" : "12px 8px", boxSizing: "border-box", overflow: "hidden" }}>
      {!canvasFullscreen && <div style={{ width: "100%", maxWidth: "960px", marginBottom: "10px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: "wrap", marginBottom: "6px" }}>
          <h1
            onClick={onLock}
            title="Lock"
            style={{ fontSize: "20px", fontWeight: "700", color: "#7fcdee", margin: 0, textShadow: "0 2px 8px rgba(100,200,255,.3)", cursor: onLock ? "pointer" : "default" }}
          ><img src="/f1sh.png" alt="" style={{ height: 20, width: "auto", verticalAlign: "middle", marginRight: 6 }} />Coral Reef Simulator</h1>
          <button onClick={reset} style={{ padding: "5px 10px", fontSize: "10px", fontFamily: "'Georgia',serif", fontWeight: "600", border: "2px solid #5a3030", borderRadius: "7px", cursor: "pointer", color: "#d08080", background: "linear-gradient(180deg,#2a1515,#1a0a0a)" }}>🔄 Reset</button>
        </div>
        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", justifyContent: "center" }}>
          {modeBtn("custom", "🔧 Custom")}
          {modeBtn("current", "⚠️ Current Path")}
          {modeBtn("save", "❦ Save Coral")}
          <button
            onClick={() => {
              setSimplified(!simplified);
              if (!simplified) {
                setWaterLevel(100);
                setStartTemp(27);
              }
            }}
            style={{
              padding: "5px 10px",
              fontSize: "10px",
              fontFamily: "'Georgia',serif",
              fontWeight: "600",
              border: simplified ? "2px solid #5cb8d6" : "2px solid #2a4a6b",
              borderRadius: "7px",
              cursor: "pointer",
              color: simplified ? "#fff" : "#8ab4d4",
              background: simplified ? "linear-gradient(180deg,#1a5a7a,#0d3a54)" : "linear-gradient(180deg,#15293e,#0e1e30)",
            }}
          >
            {simplified ? "📱 Full" : "📱 Simple"}
          </button>
        </div>
      </div>}

      {!canvasFullscreen && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: "5px", marginBottom: "8px", maxWidth: "960px", width: "100%" }}>
        <div style={statCard}><div style={L}>Ocean</div><div style={V(tempColor)}>{oT.toFixed(1)}°C</div><div style={{ fontSize: "8px", color: tC < 0 ? "#4a9adf" : "#d47a7a", marginTop: "2px" }}>{tCS}°C</div></div>
        <div style={statCard}><div style={L}>Year</div><div style={V("#7fcdee")}>{year}</div></div>
        <div style={statCard}><div style={L}>Coral ({hsL})</div><div style={V(hsC)}>{stats.aliveCorals}<span style={{ fontSize: "11px", color: "#5a8aaa" }}>/ {stats.totalCorals}</span></div><div style={{ fontSize: "7px", color: "#4a7a9a", marginTop: "2px" }}><span style={{ color: "#4aba6a" }}>{stats.healthy}✓</span> <span style={{ color: "#d4a843" }}>{stats.stressed}⚡</span> <span style={{ color: "#e07a50" }}>{stats.bleaching}◯</span> <span style={{ color: "#e04040" }}>{stats.critical}⚠</span></div></div>
        <div style={statCard}><div style={L}>Fish</div><div style={V(stats.fishPct > 60 ? "#5ac4e0" : stats.fishPct > 30 ? "#d4a843" : "#e06060")}>{stats.aliveFish}<span style={{ fontSize: "11px", color: "#5a8aaa" }}>/{stats.totalFish}</span></div>{stats.fleeingFish > 0 && <div style={{ fontSize: "7px", color: "#d4a843" }}>{stats.fleeingFish} fleeing</div>}</div>
        <div style={statCard}><div style={L}>Algae</div><div style={V(stats.avgAlgae > 60 ? "#6abf8a" : stats.avgAlgae > 30 ? "#d4a843" : "#e06060")}>{stats.avgAlgae.toFixed(1)}%</div></div>
        <div style={statCard}><div style={L}>Weather</div><div style={{ fontSize: "12px", fontWeight: "700", color: isThunderstorm ? "#e0e8ff" : isRaining ? "#9fd0e8" : "#f0d37a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{WEATHER_CONFIG[weatherType].label}</div><div style={{ fontSize: "7px", color: "#4a7a9a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Next: {WEATHER_CONFIG[nextWeatherType].label}</div></div>
        <div style={statCard}><div style={L}>Rate</div><div style={{ fontSize: "12px", fontWeight: "700", color: effectiveRate < 0 ? "#4a9adf" : "#d4a843", fontFamily: "'Courier New',monospace", overflow: "hidden", textOverflow: "ellipsis" }}>{erStr}°C/yr</div><div style={{ fontSize: "7px", color: "#4a7a9a" }}>{mode === "current" ? "SSP2-4.5" : mode === "save" ? "🧮 Local AI" : `${speed}x`}</div></div>
        <div style={statCard}><div style={L}>Ecosystem</div><div style={{ fontSize: "9px", color: "#b8d8f0", marginTop: "3px", lineHeight: 1.5 }}><div>🌱 Breeding: {stats.breedable}</div><div>🌿 Turf: {stats.turfCoverage}</div><div>🌊 Water: {waterLevel}%</div></div></div>
      </div>}

      {!canvasFullscreen && !simplified && (
        <div style={{ width: "100%", maxWidth: "960px", marginBottom: "8px" }}>
          <button onClick={() => setExpanded(!expanded)} style={{ width: "100%", padding: "5px", borderRadius: "8px", border: "1px solid #1a3050", background: "rgba(10,22,40,.8)", color: "#7a9bba", fontSize: "11px", fontFamily: "'Georgia',serif", cursor: "pointer" }}>{expanded ? "▼ Hide Details" : "▶ Show Details"}</button>
          {expanded && (
            <div style={{ marginTop: "6px", display: "flex", gap: "5px", flexWrap: "wrap", justifyContent: "center" }}>
              <div style={{ ...S, maxWidth: "200px", flex: "1 1 180px" }}><div style={L}>Coral Species</div><div style={{ display: "flex", flexWrap: "wrap", gap: "2px 6px", justifyContent: "center", marginTop: "3px" }}>{CT.map((t) => (<div key={t.n} style={{ fontSize: "8px", display: "flex", alignItems: "center", gap: "2px" }}><span style={{ width: 5, height: 5, borderRadius: "50%", background: `rgb(${t.c})`, display: "inline-block" }} /><span style={{ color: "#7a9bba" }}>{t.n}:</span><span style={{ color: "#b8d8f0", fontFamily: "'Courier New',monospace", fontWeight: "700" }}>{stats.speciesAlive[t.n]}</span></div>))}</div></div>
              <div style={{ ...S, maxWidth: "200px", flex: "1 1 180px" }}><div style={L}>Fish Species</div><div style={{ display: "flex", flexWrap: "wrap", gap: "2px 6px", justifyContent: "center", marginTop: "3px" }}>{FS.map((s) => (<div key={s.n} style={{ fontSize: "8px", display: "flex", alignItems: "center", gap: "2px" }}><span style={{ width: 5, height: 5, borderRadius: "50%", background: `rgb(${s.c})`, display: "inline-block" }} /><span style={{ color: "#7a9bba" }}>{s.n}:</span><span style={{ color: "#b8d8f0", fontFamily: "'Courier New',monospace", fontWeight: "700" }}>{stats.fishSpeciesAlive[s.n]}</span></div>))}</div></div>
              <div style={S}><div style={L}>Deaths</div><div style={{ fontSize: "9px", color: "#b8d8f0", marginTop: "3px", lineHeight: 1.5 }}>{stats.bleachingDeaths > 0 && <div>🔥 Bleach: {stats.bleachingDeaths}</div>}{stats.ageDeaths > 0 && <div>⏳ Age: {stats.ageDeaths}</div>}{stats.coldDeaths > 0 && <div>❄️ Cold: {stats.coldDeaths}</div>}{stats.frozenDeaths > 0 && <div>🧊 Frozen: {stats.frozenDeaths}</div>}{stats.evaporatedDeaths > 0 && <div>💨 Evap: {stats.evaporatedDeaths}</div>}{stats.deadCorals === 0 && <div style={{ color: "#4a7a9a" }}>None</div>}</div></div>
            </div>
          )}
        </div>
      )}

      <div
        onClick={() => setCanvasFullscreen(f => !f)}
        title={canvasFullscreen ? "Click to exit fullscreen" : "Click to expand"}
        style={{
          ...(canvasFullscreen ? {
            position: "fixed", inset: 0, zIndex: 1000,
            background: "#060e1a", display: "flex",
            alignItems: "center", justifyContent: "center",
            cursor: "zoom-out",
          } : {
            borderRadius: "12px", overflow: "hidden",
            border: `2px solid ${frz ? "#5080a0" : evp ? "#806030" : "#1a3550"}`,
            boxShadow: frz ? "0 4px 20px rgba(100,160,220,.3)" : evp ? "0 4px 20px rgba(200,100,30,.3)" : "0 4px 20px rgba(0,0,0,.5)",
            marginBottom: "12px", maxWidth: "100%", cursor: "zoom-in",
          })
        }}
      >
        <canvas ref={cvRef} width={960} height={500} style={{ display: "block", width: "100%", maxWidth: canvasFullscreen ? "none" : "960px", height: canvasFullscreen ? "100vh" : "auto", objectFit: "contain" }} />
        {canvasFullscreen && (
          <div style={{ position: "absolute", top: 12, right: 16, color: "rgba(255,255,255,.35)", fontSize: 11, fontFamily: "'Georgia',serif", pointerEvents: "none" }}>
            click to exit
          </div>
        )}
      </div>

      {!canvasFullscreen && <div style={{ width: "100%", maxWidth: "960px", background: "linear-gradient(180deg,#111e30,#0c1824)", borderRadius: "12px", padding: "12px", border: "2px solid #1a3550", boxShadow: "0 4px 16px rgba(0,0,0,.4)", boxSizing: "border-box", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
          <button onClick={() => { if (!simEnded) setPlaying(!playing); }} style={{ width: 38, height: 38, borderRadius: "50%", border: "2px solid #2a5a7a", flexShrink: 0, background: simEnded ? "#2a2a2a" : playing ? "linear-gradient(180deg,#7a3030,#4a1a1a)" : "linear-gradient(180deg,#1a5a3a,#0d3a24)", color: simEnded ? "#666" : "#fff", fontSize: 15, cursor: simEnded ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 3px 10px rgba(0,0,0,.4)", opacity: simEnded ? 0.5 : 1 }}>{simEnded ? "⏹" : playing ? "⏸" : "▶"}</button>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 }}>
            {slider("Year", SY, EY, year, (e) => scrubToYear(+e.target.value), year, "#7fcdee")}
            {mode === "custom" && slider("Temp", -500, 500, Math.round(rate * 100), (e) => setRate(+e.target.value / 100), `${rate >= 0 ? "+" : ""}${rate.toFixed(2)}°C/yr`, rate < -1 ? "#4a8ae0" : rate < 0 ? "#4a9adf" : rate < 0.5 ? "#d4a843" : "#e06060", "linear-gradient(90deg,#2a4ae0 0%,#2a8a5a 40%,#4a9a6a 50%,#d4a843 60%,#c04040 80%,#a02020 100%)")}
            {!simplified && slider("Start°C", 0, 50, Math.round(startTemp), (e) => setStartTemp(+e.target.value), `${startTemp.toFixed(0)}°C`, startTemp < 15 ? "#60a8e0" : startTemp > 30 ? "#e06040" : "#5ac4e0", "linear-gradient(90deg,#2a4ae0 0%,#2a8a5a 30%,#4a9a6a 50%,#d4a843 70%,#c04040 100%)")}
            {!simplified && slider("Water", 0, 100, waterLevel, (e) => setWaterLevel(+e.target.value), `${waterLevel}%`, waterLevel > 70 ? "#5ab8d8" : waterLevel > 30 ? "#d4a843" : "#e06060", "linear-gradient(90deg,#c04040 0%,#d4a843 30%,#2a8a5a 60%,#1a6a8a 100%)")}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", paddingTop: "6px", borderTop: "1px solid #1a3050" }}>
          <span style={{ fontSize: "8px", color: "#5a8aaa", fontWeight: "600", letterSpacing: "1px", textTransform: "uppercase", marginRight: "4px" }}>Speed</span>
          {[0.5, 1, 2, 5, 10, 20].map((s) => (
            <button key={s} onClick={() => setSpeed(s)} style={{ padding: "4px 8px", fontSize: "9px", fontFamily: "'Courier New',monospace", fontWeight: "700", border: speed === s ? "1.5px solid #5cb8d6" : "1.5px solid #1e3050", borderRadius: "6px", background: speed === s ? "rgba(30,80,120,.6)" : "rgba(10,20,35,.5)", color: speed === s ? "#7fcdee" : "#4a6a8a", cursor: "pointer", transition: "all .15s" }}>{s}x</button>
          ))}
        </div>
      </div>}

      {!canvasFullscreen && <p style={{ fontSize: "8px", color: "#3a5a7a", marginTop: "10px", textAlign: "center", fontStyle: "italic", maxWidth: "720px", lineHeight: 1.3 }}>
        Boids flocking · Clownfish · Fish swim in when healthy, flee when dying · Max {MAX_CORALS.toLocaleString()} coral · SSP2-4.5 / Local Adaptive AI · {speed}x speed
      </p>}
    </div>
  );
}
